// Settings → Account (moderation phase 2 arc 4; policy §6.3–§6.4). Four
// sections: the account's standing (state, end date, active strikes), every
// moderation decision the person was told of with Appeal on each, "Your
// reports", and Download my data.
//
// Rendered by SettingsPage, and on its own by TerminatedAccountGate: a
// terminated person reaches this page and nothing else of the app, so it
// must not depend on anything the terminated read gate refuses.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AccountStatusService from '../../services/accountStatusService';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import SectionHeading from '../shared/SectionHeading';
import Toast from '../shared/Toast';
import useToast from '../../hooks/useToast';

const MAX_BODY = 2000;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCOUNT_KIND = 'account';
const WARNING_KIND = 'warning';
const LINK_KIND = 'link';
const PENDING = 'pending';

/** SQLite UTC datetime text → a local date in the reader's language. */
function formatDate(text, language, withTime = false) {
  if (!text) return '';
  const s = String(text);
  const d = new Date(/[zZ]$/.test(s) ? s : `${s.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return s;
  const opts = withTime
    ? { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { day: 'numeric', month: 'long', year: 'numeric' };
  try {
    return d.toLocaleString(language || undefined, opts);
  } catch {
    return d.toLocaleDateString();
  }
}

const listClass = 'bg-surface border border-border rounded-card divide-y divide-border';

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function useDecisionTitle() {
  const { t } = useTranslation('settings');
  const typeLabel = (type) => t(`accountStatus.contentTypes.${type}`, { defaultValue: t('accountStatus.contentTypes.fallback') });
  return (d) => {
    if (d.kind === ACCOUNT_KIND) {
      const key = {
        restricted: 'accountRestricted',
        suspended: 'accountSuspended',
        terminated: 'accountTerminated',
        strike: 'accountStrike',
        reports_paused: 'accountReportsPaused'
      }[d.action];
      return t(`accountStatus.decisions.title.${key || 'fallback'}`);
    }
    if (d.kind === WARNING_KIND) {
      // A warning about the reports a person sends (arc 6) is about them as a
      // reporter, not about their profile.
      if (d.content_type === 'user' && d.reason_category === 'report_abuse') return t('accountStatus.decisions.title.warningReports');
      return t('accountStatus.decisions.title.warning', { type: typeLabel(d.content_type).toLowerCase() });
    }
    return t(`accountStatus.decisions.title.${d.action}`, {
      type: typeLabel(d.content_type),
      defaultValue: t('accountStatus.decisions.title.fallback')
    });
  };
}

function AppealModal({ decision, answerDays, onClose, onSent }) {
  const { t } = useTranslation('settings');
  const titleOf = useDecisionTitle();
  const [body, setBody] = useState('');
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setError(t('accountStatus.appealModal.imageTooBig'));
      return;
    }
    try {
      setImage(await readAsDataUrl(file));
      setError(null);
    } catch {
      setError(t('accountStatus.appealModal.failed'));
    }
  };

  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setError(null);
    const result = await AccountStatusService.fileAppeal(decision.action_id, body.trim(), image);
    setSending(false);
    if (result.success) {
      onSent(decision.action_id);
      return;
    }
    setError(result.error || t('accountStatus.appealModal.failed'));
  };

  return (
    <Modal isOpen onClose={onClose} size="md" ariaLabel={t('accountStatus.appealModal.ariaLabel')} mobileFullScreen>
      <div className="p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-content-primary mb-1">{t('accountStatus.appealModal.title')}</h2>
        <p className="text-sm font-medium text-content-primary">
          {titleOf(decision)}
          {decision.content_name ? ` · "${decision.content_name}"` : ''}
        </p>
        <p className="text-sm text-content-secondary mt-2 mb-4">
          {t('accountStatus.appealModal.intro', { days: answerDays })}
        </p>

        <label htmlFor="appeal-body" className="block text-sm font-medium text-content-primary mb-1">
          {t('accountStatus.appealModal.bodyLabel')}
        </label>
        <textarea
          id="appeal-body"
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
          rows={6}
          maxLength={MAX_BODY}
          placeholder={t('accountStatus.appealModal.bodyPlaceholder')}
          className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-surface text-content-primary focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2"
        />
        <p className="text-xs text-content-muted text-right tabular-nums mt-1">
          {t('accountStatus.appealModal.counter', { count: body.length, max: MAX_BODY })}
        </p>

        <div className="mt-3">
          <p className="text-sm font-medium text-content-primary">{t('accountStatus.appealModal.imageLabel')}</p>
          <p className="text-xs text-content-secondary mb-2">{t('accountStatus.appealModal.imageHelp')}</p>
          {image ? (
            <div className="flex items-start gap-3">
              <img src={image} alt={t('accountStatus.appealModal.imageAlt')} className="w-24 h-24 object-cover rounded-lg border border-border" />
              <Button variant="secondary" size="sm" onClick={() => setImage(null)}>
                {t('accountStatus.appealModal.imageRemove')}
              </Button>
            </div>
          ) : (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="sr-only" onChange={pickImage} aria-label={t('accountStatus.appealModal.imageLabel')} />
              <Button variant="secondary" size="sm" icon="camera" onClick={() => fileRef.current?.click()}>
                {t('accountStatus.appealModal.imagePick')}
              </Button>
            </>
          )}
        </div>

        {error && <p role="alert" className="text-sm text-error mt-3">{error}</p>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={onClose}>{t('accountStatus.appealModal.cancel')}</Button>
          <Button onClick={send} disabled={!body.trim()} loading={sending}>
            {sending ? t('accountStatus.appealModal.sending') : t('accountStatus.appealModal.send')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function OwnAppealImage({ appealId }) {
  const { t } = useTranslation('settings');
  const [url, setUrl] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  // Re-armed in the body for StrictMode's second mount.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  // A row that unmounts mid-fetch releases the image it would have shown (C147).
  const show = async () => {
    const next = await AccountStatusService.getAppealImageUrl(appealId);
    if (!alive.current) {
      if (next) URL.revokeObjectURL(next);
      return;
    }
    setUrl(next);
  };
  if (url) {
    return (
      <div className="mt-2 space-y-1">
        <img src={url} alt="" className="max-h-48 rounded-lg border border-border" />
        <Button variant="ghost" size="sm" onClick={() => setUrl(null)}>{t('accountStatus.decisions.appeal.hideImage')}</Button>
      </div>
    );
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={show}
    >
      {t('accountStatus.decisions.appeal.showImage')}
    </Button>
  );
}

function DecisionRow({ decision: d, onAppeal }) {
  const { t, i18n } = useTranslation('settings');
  const titleOf = useDecisionTitle();
  const date = (s) => formatDate(s, i18n.language);
  const a = d.appeal;
  const statusLine = d.status === 'reversed'
    ? (d.reversed?.grounds
      ? t('accountStatus.decisions.status.reversedWithGrounds', { date: date(d.reversed.at), grounds: d.reversed.grounds })
      : t('accountStatus.decisions.status.reversed', { date: date(d.reversed?.at) }))
    : d.status === 'ended' ? t('accountStatus.decisions.status.ended')
      // Closed by a correction made outside the app (C152): never told, so no date or grounds.
      : d.status === 'closed' ? t('accountStatus.decisions.status.closed')
        : t('accountStatus.decisions.status.inForce');

  return (
    <li className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-content-primary">{titleOf(d)}</p>
          {(d.content_name || !d.yours) && (
            <p className="text-sm text-content-secondary break-words">
              {d.content_name ? `"${d.content_name}"` : ''}
              {d.content_name && !d.yours ? ' · ' : ''}
              {!d.yours ? t('accountStatus.decisions.notYours') : ''}
            </p>
          )}
        </div>
        <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${d.status === 'in_force' ? 'bg-warning-light text-warning-dark' : 'bg-surface-secondary text-content-secondary'}`}>
          {d.status === 'reversed' ? t('accountStatus.decisions.status.reversedBadge') : statusLine}
        </span>
      </div>

      <p className="text-xs text-content-muted mt-1">
        {t('accountStatus.decisions.decided', { date: date(d.decided_at) })}
        {d.reason_label ? ` · ${t('accountStatus.decisions.reason', { reason: d.reason_label })}` : ''}
        {d.ends_at ? ` · ${t('accountStatus.decisions.until', { date: date(d.ends_at) })}` : ''}
        {d.automated
          // A ladder step (arc 5): a published rule applied after a person's decision.
          ? ` · ${t(d.kind === ACCOUNT_KIND ? 'accountStatus.decisions.appliedByRule' : 'accountStatus.decisions.automated')}`
          : ''}
      </p>
      {d.grounds && (
        <p className="text-sm text-content-secondary mt-2 break-words">
          {t('accountStatus.decisions.details', { grounds: d.grounds })}
        </p>
      )}
      {d.status === 'reversed' && <p className="text-sm text-content-secondary mt-2 break-words">{statusLine}</p>}

      {a && (
        <div className="mt-3 text-sm text-content-secondary space-y-1">
          {a.status === 'open' && (
            <p>{a.mine ? t('accountStatus.decisions.appeal.openMine', { date: date(a.filed_at) }) : t('accountStatus.decisions.appeal.openOther')}</p>
          )}
          {a.status === 'upheld' && <p>{t('accountStatus.decisions.appeal.upheld', { date: date(a.reviewed_at) })}</p>}
          {/* A decision a system correction closed (C160): nothing left to decide. */}
          {a.status === 'closed' && (
            <>
              <p>{t('accountStatus.decisions.appeal.closed', { date: date(a.reviewed_at) })}</p>
              {a.mine && a.outcome_grounds && <p className="break-words">{t('accountStatus.decisions.appeal.closedNote', { grounds: a.outcome_grounds })}</p>}
            </>
          )}
          {a.status === 'rejected' && (
            <>
              <p>{t('accountStatus.decisions.appeal.rejected', { date: date(a.reviewed_at) })}</p>
              {a.outcome_grounds && <p className="break-words">{t('accountStatus.decisions.appeal.rejectedGrounds', { grounds: a.outcome_grounds })}</p>}
            </>
          )}
          {a.mine && a.body && (
            <p className="text-content-muted break-words whitespace-pre-line">{t('accountStatus.decisions.appeal.yourWords', { body: a.body })}</p>
          )}
          {a.mine && a.has_image && <OwnAppealImage appealId={a.id} />}
        </div>
      )}

      {d.appealable && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => onAppeal(d)}>{t('accountStatus.decisions.appeal.button')}</Button>
        </div>
      )}
      {!d.appealable && d.appeal_refusal === 'expired' && (
        <p className="text-xs text-content-muted mt-2">{t('accountStatus.decisions.appeal.expired', { date: date(d.appeal_until) })}</p>
      )}
    </li>
  );
}

function ReportRow({ report: r }) {
  const { t, i18n } = useTranslation('settings');
  const what = r.kind === LINK_KIND
    ? (r.link_host
      ? t('accountStatus.reports.link', { subject: r.subject, host: r.link_host })
      : t('accountStatus.reports.linkNoHost', { subject: r.subject }))
    : r.subject;
  let outcome;
  if (r.status === PENDING) outcome = t('accountStatus.reports.pending');
  else if (r.kind === LINK_KIND) outcome = r.status === 'actioned' ? t('accountStatus.reports.linkActioned') : t('accountStatus.reports.linkDismissed');
  else outcome = r.outcome?.message || t('accountStatus.reports.reviewed');
  return (
    <li className="p-4">
      <p className="font-medium text-content-primary break-words">{what}</p>
      <p className="text-xs text-content-muted mt-1">
        {t('accountStatus.reports.filed', { date: formatDate(r.filed_at, i18n.language) })}
        {r.reason ? ` · ${t(`accountStatus.reports.reasons.${r.reason}`, { defaultValue: r.reason })}` : ''}
      </p>
      <p className="text-sm text-content-secondary mt-2 break-words">{outcome}</p>
    </li>
  );
}

function AccountStatusTab() {
  const { t, i18n } = useTranslation('settings');
  const { toast, showToast, hideToast } = useToast();
  const [status, setStatus] = useState(null);
  const [reports, setReports] = useState(null);
  const [failed, setFailed] = useState(false);
  const [appealing, setAppealing] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [exportNote, setExportNote] = useState(null);

  const load = useCallback(async () => {
    setFailed(false);
    const [s, r] = await Promise.all([AccountStatusService.getStatus(), AccountStatusService.getReports()]);
    if (s.success) setStatus(s.status);
    else setFailed(true);
    setReports(r.success ? r.reports : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    setExportNote(null);
    const result = await AccountStatusService.downloadData();
    setDownloading(false);
    if (result.success) {
      showToast(t('accountStatus.data.started'), 'success');
      load();
    } else if (result.code === 'export_limited') {
      setExportNote(t('accountStatus.data.limited', { date: formatDate(result.availableAt, i18n.language, true) }));
    } else {
      setExportNote(result.error || t('accountStatus.data.failed'));
    }
  };

  if (failed && !status) {
    return (
      <div role="alert" className="text-center py-10">
        <p className="text-sm text-content-secondary mb-3">{t('accountStatus.loadFailed')}</p>
        <Button variant="secondary" onClick={load}>{t('accountStatus.retry')}</Button>
      </div>
    );
  }
  if (!status) {
    return <p className="text-sm text-content-secondary text-center py-10" role="status">{t('accountStatus.loading')}</p>;
  }

  const date = (s) => formatDate(s, i18n.language);
  const standing = status.state === 'restricted' ? t('accountStatus.standing.restricted', { date: date(status.until) })
    : status.state === 'suspended' ? t('accountStatus.standing.suspended', { date: date(status.until) })
      : status.state === 'terminated' ? t('accountStatus.standing.terminated')
        : t('accountStatus.standing.good');
  const limitedUntil = exportNote ? null : status.export_available_at;

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      <section aria-labelledby="account-standing">
        <SectionHeading id="account-standing" title={t('accountStatus.standing.heading')} />
        <div className={`rounded-card border p-4 ${status.state === 'good' ? 'bg-surface border-border' : 'bg-warning-light border-warning/30'}`}>
          <p className={`text-sm ${status.state === 'good' ? 'text-content-primary' : 'text-warning-dark'}`}>{standing}</p>
          {/* A report pause (arc 6) is not a state: its own line. */}
          {status.reports_paused_until && (
            <p className="text-sm text-warning-dark mt-2">{t('accountStatus.standing.reportsPaused', { date: date(status.reports_paused_until) })}</p>
          )}
          {status.strikes?.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-content-primary">{t('accountStatus.standing.strikes', { count: status.strikes.length })}</p>
              <ul className="mt-1 space-y-0.5">
                {status.strikes.map((s) => (
                  <li key={s.action_id} className="text-sm text-content-secondary">
                    {t('accountStatus.standing.strikeLine', { decided: date(s.decided_at), expires: date(s.expires_at) })}
                  </li>
                ))}
              </ul>
              {/* "Strikes until the next step" (decision 12): only once the
                  ladder and its help page are published (decision 16). */}
              {status.next_step && (
                <p className="text-sm text-content-secondary mt-2">
                  {t(`accountStatus.standing.nextStep.${status.next_step.termination_review ? 'review' : status.next_step.action}`, { days: status.next_step.days })}
                </p>
              )}
            </div>
          )}
          {status.ladder_live && status.next_step?.help_path && (
            <Link to={status.next_step.help_path} className="inline-block mt-3 text-sm font-medium text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 rounded">
              {t('accountStatus.standing.howItWorks')}
            </Link>
          )}
        </div>
      </section>

      <section aria-labelledby="account-decisions">
        <SectionHeading id="account-decisions" title={t('accountStatus.decisions.heading')} />
        <p className="text-sm text-content-secondary mb-3">
          {t('accountStatus.decisions.intro', { months: status.appeal_window_months, days: status.answer_days })}
        </p>
        {status.decisions.length === 0 ? (
          <p className="text-sm text-content-secondary">{t('accountStatus.decisions.empty')}</p>
        ) : (
          <ul className={listClass}>
            {status.decisions.map((d) => <DecisionRow key={d.action_id} decision={d} onAppeal={setAppealing} />)}
          </ul>
        )}
      </section>

      <section aria-labelledby="account-reports">
        <SectionHeading id="account-reports" title={t('accountStatus.reports.heading')} />
        <p className="text-sm text-content-secondary mb-3">{t('accountStatus.reports.intro')}</p>
        {status.reports_paused_until && (
          <p className="text-sm text-warning-dark bg-warning-light border border-warning/30 rounded-lg p-3 mb-3">
            {t('accountStatus.reports.paused', { date: date(status.reports_paused_until) })}
          </p>
        )}
        {!reports || reports.length === 0 ? (
          <p className="text-sm text-content-secondary">{t('accountStatus.reports.empty')}</p>
        ) : (
          <ul className={listClass}>
            {reports.map((r) => <ReportRow key={`${r.kind}-${r.id}`} report={r} />)}
          </ul>
        )}
      </section>

      <section aria-labelledby="account-data">
        <SectionHeading id="account-data" title={t('accountStatus.data.heading')} />
        <p className="text-sm text-content-secondary mb-3">{t('accountStatus.data.intro')}</p>
        <Button variant="secondary" icon="download" onClick={download} loading={downloading} disabled={!!limitedUntil}>
          {downloading ? t('accountStatus.data.preparing') : t('accountStatus.data.button')}
        </Button>
        {(exportNote || limitedUntil) && (
          <p className="text-sm text-content-secondary mt-2" role="status">
            {exportNote || t('accountStatus.data.limited', { date: formatDate(limitedUntil, i18n.language, true) })}
          </p>
        )}
      </section>

      {appealing && (
        <AppealModal
          decision={appealing}
          answerDays={status.answer_days}
          onClose={() => setAppealing(null)}
          onSent={(actionId) => {
            // Only this appeal's own dialog: one cancelled while it sent must
            // not close the next one opened (C147).
            setAppealing((current) => (current && current.action_id === actionId ? null : current));
            showToast(t('accountStatus.appealModal.sent', { days: status.answer_days }), 'success');
            load();
          }}
        />
      )}
      {toast && <Toast {...toast} onClose={hideToast} />}
    </div>
  );
}

export default AccountStatusTab;
